import React, { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Chip } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import AppTable from "../../shared/AppTable";
import AppButton from "../../shared/AppButton";
import AppModal from "../../components/AppModal";
import Pagination from "../../components/Pagination";
import CustomFilterInput from "../../shared/CustomFilterInput";
import TableSkeletonLoader from "../../common/TableLoading";
import {
  getIntegrationLogHistory,
  getIntegrationLogs,
  getIntegrationLogsSummary,
  getWebhookOutboxLogs,
  retryWebhookOutboxById,
} from "./integrationLogsApi";
import { useTranslation } from "react-i18next";

const tabs = [
  { key: "summary", label: "Summary" },
  { key: "logs", label: "Logs" },
  { key: "history", label: "History" },
  { key: "webhook", label: "Webhook Outbox" },
];

const entityOptions = [
  { id: "preauth", name: "PreAuth" },
  { id: "claim", name: "Claim" },
];

const directionOptions = [
  { id: "inbound", name: "Inbound" },
  { id: "outbound", name: "Outbound" },
];

const statusOptions = [
  { id: "received", name: "Received" },
  { id: "blocked", name: "Blocked" },
  { id: "stored", name: "Stored" },
  { id: "processing", name: "Processing" },
  { id: "processed", name: "Processed" },
  { id: "failed", name: "Failed" },
  { id: "queued", name: "Queued" },
  { id: "delivered", name: "Delivered" },
  { id: "retry_scheduled", name: "Retry Scheduled" },
  { id: "dead", name: "Dead" },
];

const defaultFilters = {
  from_date: "",
  to_date: "",
  entity_type: "",
  direction: "",
  status: "",
  name: "",
  document_id: "",
  request_id: "",
  transaction_id: "",
  include_payload: "",
  limit: 20,
  page: 1,
};

const DateTimeStack = ({ value }) => {
  if (!value) return <span className="text-xs text-slate-600">---</span>;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return <span className="text-xs text-slate-600">---</span>;
  }

  return (
    <span className="block whitespace-nowrap text-xs leading-tight text-slate-600">
      <span className="block font-semibold text-slate-700">
        {date.toLocaleDateString()}
      </span>
      <span className="block text-[11px] text-slate-500">
        {date.toLocaleTimeString()}
      </span>
    </span>
  );
};

const textValue = (value) => value || "---";

const documentValue = (row) => row?.document_id || row?.patient_document_id;

const StatusChip = ({ status }) => {
  const isGood = ["processed", "delivered", "stored"].includes(status);
  const isBad = ["failed", "blocked", "dead", "cleanup_failed"].includes(status);
  return (
    <Chip
      label={status || "---"}
      size="small"
      sx={{
        height: 22,
        borderRadius: "6px",
        fontSize: "11px",
        fontWeight: 700,
        backgroundColor: isGood ? "#dcfce7" : isBad ? "#fee2e2" : "#e0f2fe",
        color: isGood ? "#166534" : isBad ? "#991b1b" : "#075985",
        textTransform: "uppercase",
      }}
    />
  );
};

function IntegrationLogs() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("summary");
  const [filters, setFilters] = useState(defaultFilters);
  const [appliedFilters, setAppliedFilters] = useState(defaultFilters);
  const [rows, setRows] = useState([]);
  const [pages, setPages] = useState(1);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const requestParams = useMemo(
    () => ({
      ...appliedFilters,
      include_payload:
        appliedFilters.include_payload === "true" ? "true" : "",
    }),
    [appliedFilters],
  );

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      let response;
      if (activeTab === "summary") {
        response = await getIntegrationLogsSummary(requestParams);
      } else if (activeTab === "webhook") {
        response = await getWebhookOutboxLogs(requestParams);
      } else {
        response = await getIntegrationLogs(requestParams);
      }
      setRows(response?.data || []);
      setPages(response?.pages || 1);
      setCount(response?.count || 0);
    } catch (error) {
      toast.error(
        error?.response?.data?.error ||
          error?.response?.data?.message ||
          error.message ||
          t("Failed to fetch integration logs"),
      );
    } finally {
      setLoading(false);
    }
  }, [activeTab, requestParams, t]);

  useEffect(() => {
    if (activeTab !== "history") {
      loadData();
    }
  }, [activeTab, loadData]);

  const updateFilter = (event) => {
    const { name, value, type, checked } = event.target;
    setFilters((prev) => ({
      ...prev,
      page: 1,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setRows([]);
    setPages(1);
    setCount(0);
    setHistoryRows([]);
    setFilters(defaultFilters);
    setAppliedFilters(defaultFilters);
  };

  const handleApplyFilters = () => {
    const nextFilters = { ...filters, page: 1 };
    setFilters(nextFilters);
    setAppliedFilters(nextFilters);
    if (activeTab === "history") {
      loadHistory(nextFilters);
      return;
    }
  };

  const handleResetFilters = () => {
    setFilters(defaultFilters);
    setAppliedFilters(defaultFilters);
    if (activeTab === "history") {
      setHistoryRows([]);
    }
  };

  const loadHistory = async (sourceFilters = appliedFilters) => {
    if (!sourceFilters.request_id && !sourceFilters.transaction_id) {
      toast.error(t("Request ID or Transaction ID is required"));
      return;
    }

    try {
      setHistoryLoading(true);
      const response = await getIntegrationLogHistory({
        ...sourceFilters,
        include_payload:
          sourceFilters.include_payload === "true" ? "true" : "",
      });
      setHistoryRows(response?.data || []);
    } catch (error) {
      toast.error(
        error?.response?.data?.error ||
          error?.response?.data?.message ||
          error.message ||
          t("Failed to fetch integration log history"),
      );
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleView = async (row) => {
    setSelectedRow(row);
    setShowDetailsModal(true);
  };

  const handleRetryWebhook = async (row) => {
    try {
      await retryWebhookOutboxById(row._id);
      toast.success(t("Webhook retry has been scheduled successfully"));
      loadData();
    } catch (error) {
      toast.error(
        error?.response?.data?.error ||
          error?.response?.data?.message ||
          error.message ||
          t("Failed to schedule webhook retry"),
      );
    }
  };

  const baseColumns = [
    {
      header: t("Status"),
      render: (row) => <StatusChip status={row.status} />,
    },
    { header: t("Entity"), field: "entity_type" },
    {
      header: t("Patient Name"),
      render: (row) => (
        <span
          className="block text-xs font-medium text-slate-700"
          title={row?.patient_name || ""}
        >
          {textValue(row?.patient_name)}
        </span>
      ),
    },
    {
      header: t("Document ID"),
      render: (row) => (
        <span
          className="block text-xs font-medium text-slate-700"
          title={documentValue(row) || ""}
        >
          {textValue(documentValue(row))}
        </span>
      ),
    },
    { header: t("Direction"), field: "direction" },
    {
      header: t("Transaction ID"),
      render: (row) => (
        <span className="block max-w-[190px] truncate text-xs font-medium text-slate-700">
          {textValue(row.transaction_id)}
        </span>
      ),
    },
    {
      header: t("Request ID"),
      render: (row) => (
        <span className="block max-w-[190px] truncate text-xs text-slate-600">
          {textValue(row.request_id)}
        </span>
      ),
    },
    {
      header: t("Endpoint"),
      render: (row) => (
        <span className="block max-w-[260px] truncate text-xs text-slate-600">
          {textValue(row.endpoint)}
        </span>
      ),
    },
    {
      header: t("Created At"),
      render: (row) => <DateTimeStack value={row.created_at} />,
    },
  ];

  const webhookColumns = [
    {
      header: t("Status"),
      render: (row) => <StatusChip status={row.status} />,
    },
    { header: t("Entity"), field: "entity_type" },
    {
      header: t("Transaction ID"),
      render: (row) => (
        <span className="block max-w-[190px] truncate text-xs font-medium text-slate-700">
          {textValue(row.transaction_id)}
        </span>
      ),
    },
    {
      header: t("Webhook URL"),
      render: (row) => (
        <span className="block max-w-[320px] truncate text-xs text-slate-600">
          {textValue(row.webhook_url)}
        </span>
      ),
    },
    {
      header: t("Attempts"),
      render: (row) => `${row.attempts || 0}/${row.max_attempts || 0}`,
    },
    {
      header: t("Next Attempt"),
      render: (row) => <DateTimeStack value={row.next_attempt_at} />,
    },
    {
      header: t("Updated At"),
      render: (row) => <DateTimeStack value={row.updated_at} />,
    },
  ];

  const columns = activeTab === "webhook" ? webhookColumns : baseColumns;
  const tableRows = activeTab === "history" ? historyRows : rows;
  const tableLoading = activeTab === "history" ? historyLoading : loading;

  return (
    <div className="w-full !p-0">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <span className="font-semibold text-black/70 text-lg">
          {t("Integration Logs")}
          {activeTab !== "history" && (
            <span className="text-lg ml-1">
              ({count}{" "}
              <span className="text-xs font-bold italic">{t("Entries")}</span>)
            </span>
          )}
        </span>
        <AppButton
          label={t("Refresh")}
          startIcon={<RefreshIcon />}
          onClick={activeTab === "history" ? loadHistory : loadData}
          isLoading={tableLoading}
          loadingText={t("Refreshing")}
        />
      </div>

      <div className="my-2">
        <ul className="flex flex-wrap gap-0.5 rounded-lg bg-gray-100 p-1 text-center text-gray-500 shadow-inner">
          {tabs.map((tab) => (
            <li key={tab.key} className="flex-1 min-w-[140px]">
              <button
                type="button"
                onClick={() => handleTabChange(tab.key)}
                className={`flex w-full justify-center rounded-lg px-2 py-0.5 text-[13px] transition-colors hover:text-[#0070C2] ${
                  activeTab === tab.key ? "bg-[#0070C2] !text-white" : ""
                }`}
              >
                {t(tab.label)}
              </button>
            </li>
          ))}
        </ul>
      </div>

      <div className="mb-2 rounded-lg border border-slate-200 bg-white p-2">
        <div className="grid grid-cols-2 items-end gap-x-1.5 gap-y-1.5 md:grid-cols-4 lg:grid-cols-8 xl:grid-cols-10">
          <CustomFilterInput
            label={t("Submission Date")}
            name="from_date"
            type="date"
            value={filters.from_date}
            onChange={updateFilter}
            placeholder={t("From Date")}
          />
          <CustomFilterInput
            label={t("To Date")}
            name="to_date"
            type="date"
            value={filters.to_date}
            onChange={updateFilter}
            placeholder={t("To Date")}
          />
          <CustomFilterInput
            label={t("Select Entity")}
            name="entity_type"
            value={filters.entity_type}
            onChange={updateFilter}
            placeholder={t("Select Entity")}
            isSelect
            options={entityOptions}
          />
          {activeTab !== "webhook" && (
            <CustomFilterInput
              label={t("Select Direction")}
              name="direction"
              value={filters.direction}
              onChange={updateFilter}
              placeholder={t("Select Direction")}
              isSelect
              options={directionOptions}
            />
          )}
          <CustomFilterInput
            label={t("Select Status")}
            name="status"
            value={filters.status}
            onChange={updateFilter}
            placeholder={t("Select Status")}
            isSelect
            options={statusOptions}
          />
          <CustomFilterInput
            label={t("Patient Name")}
            name="name"
            value={filters.name}
            onChange={updateFilter}
            placeholder={t("Patient Name")}
          />
          <CustomFilterInput
            label={t("Document ID")}
            name="document_id"
            value={filters.document_id}
            onChange={updateFilter}
            placeholder={t("Document ID")}
          />
          <CustomFilterInput
            label={t("Transaction ID")}
            name="transaction_id"
            value={filters.transaction_id}
            onChange={updateFilter}
            placeholder={t("Transaction ID")}
          />
          <CustomFilterInput
            label={t("Request ID")}
            name="request_id"
            value={filters.request_id}
            onChange={updateFilter}
            placeholder={t("Request ID")}
          />
          {activeTab !== "webhook" && (
            <CustomFilterInput
              label={t("Payload")}
              name="include_payload"
              value={filters.include_payload}
              onChange={updateFilter}
              placeholder={t("Exclude Payload")}
              isSelect
              options={[{ id: "true", name: t("Include Payload") }]}
            />
          )}

          <div className="flex items-end gap-1">
              <button
              type="button"
              title={t("Apply Filters")}
              aria-label={t("Apply Filters")}
              onClick={handleApplyFilters}
              className="flex h-[29px] w-[29px] items-center justify-center rounded-[4px] bg-[#0070C2] text-white hover:bg-[#005fa3]"
            >
              <FilterAltOutlinedIcon sx={{ fontSize: 18 }} />
            </button>
            <button
              type="button"
              title={t("Reset")}
              aria-label={t("Reset")}
              onClick={handleResetFilters}
              className="flex h-[29px] w-[29px] items-center justify-center rounded-[4px] border border-gray-300 bg-white text-gray-600 hover:bg-gray-100"
            >
              <RestartAltIcon sx={{ fontSize: 18 }} />
            </button>
          </div>
        </div>
      </div>

      <AppTable
        columns={columns}
        data={tableRows}
        loading={tableLoading}
        onView={handleView}
        onProcess={activeTab === "webhook" ? handleRetryWebhook : null}
        cellClassName="!py-0"
        actioncellClassName="!py-0"
        bodyRowPadding="7px"
        LoaderComponent={
          <TableSkeletonLoader columnCount={columns.length + 1} rowCount={10} />
        }
        containerClassName="custom-table-scrollbar"
      />

      {activeTab !== "history" && pages > 1 && (
        <div className="mt-2">
          <Pagination
            currentPage={filters.page}
            totalPages={pages}
            onPageChange={(page) =>
              {
                setFilters((prev) => ({ ...prev, page }));
                setAppliedFilters((prev) => ({ ...prev, page }));
              }
            }
          />
        </div>
      )}

      <AppModal
        open={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        title={t("Integration Log Details")}
        maxWidth="lg"
        contentSx={{ backgroundColor: "#f8fafc" }}
      >
        {selectedRow && (
          <div className="space-y-3">
            <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
              {[
                ["Status", selectedRow.status],
                ["Entity", selectedRow.entity_type],
                ["Patient Name", selectedRow.patient_name],
                ["Document ID", documentValue(selectedRow)],
                ["Direction", selectedRow.direction],
                ["Transaction ID", selectedRow.transaction_id],
                ["Request ID", selectedRow.request_id],
                ["Created At", <DateTimeStack value={selectedRow.created_at} />],
                ["Updated At", <DateTimeStack value={selectedRow.updated_at} />],
                ["Endpoint", selectedRow.endpoint || selectedRow.webhook_url],
                ["Error", selectedRow.error_message || selectedRow.last_error],
              ].map(([label, value]) => (
                <div
                  key={label}
                  className="rounded-md border border-slate-200 bg-white px-2 py-1.5"
                >
                  <p className="text-[10px] font-bold uppercase text-slate-500">
                    {t(label)}
                  </p>
                  <div className="mt-0.5 break-words text-[12px] font-semibold text-slate-700">
                    {React.isValidElement(value) ? value : textValue(value)}
                  </div>
                </div>
              ))}
            </div>

            <div className="rounded-md border border-slate-200 bg-white p-2">
              <p className="mb-1 text-[10px] font-bold uppercase text-slate-500">
                {t("Raw Data")}
              </p>
              <pre className="max-h-[55vh] overflow-auto rounded-md bg-slate-950 p-3 font-mono text-[13px] leading-relaxed text-slate-100">
                {JSON.stringify(selectedRow, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </AppModal>
    </div>
  );
}

export default IntegrationLogs;
